import { useEffect, useState } from 'react'
import { sdk } from '@/lib/sdk'
import { isYearly, yearlyInstallments } from '@/lib/checkout/annual'
import { parseAllowedPaymentMethods } from '@/lib/checkout/paymentMethods'
import type { CalculatePriceResponse } from '@/types/checkout'

/** What a yearly option needs to be quoted on the card. */
export interface QuotableOption {
  id: string
  billingInterval?: string | null
  maxInstallments?: number | string | null
  allowedPaymentMethods?: unknown
  price?: number | string
}

/** The card terms of a yearly option, as the server prices them (installments with any interest). */
export interface CardQuote {
  installments: number
  each: number
  total: number
  /** No interest for the buyer: the organization absorbs it, or the table has none. */
  interestFree: boolean
  percent: number
  /** The card also takes the whole amount at 1× with no surcharge. */
  singleChargeFree: boolean
}

/** Installments a yearly option offers on the card, when it takes cards at all; 1 otherwise. */
export const cardInstallments = (option: QuotableOption): number =>
  parseAllowedPaymentMethods(option.allowedPaymentMethods).includes('CREDIT_CARD') ? yearlyInstallments(option) : 1

/** The "PIX ou boleto" label of an option's up-front methods, or null when it takes neither. */
export const upfrontMethodsKey = (allowedPaymentMethods: unknown): string | null => {
  const methods = parseAllowedPaymentMethods(allowedPaymentMethods)
  if (methods.includes('PIX') && methods.includes('BOLETO')) return 'checkout:annualPlan.methods.pixOrBoleto'
  if (methods.includes('PIX')) return 'checkout:annualPlan.methods.pix'
  if (methods.includes('BOLETO')) return 'checkout:annualPlan.methods.boleto'
  return null
}

/** Which "Save X%" the option shows: plain, or naming the methods that pay the up-front price. */
export type SavingsVariant = 'plain' | 'pixOrBoleto' | 'pix' | 'boleto' | 'card'

/**
 * The one rule behind the green saving tag (pricing checkout, Store, padlocked
 * space, plan change). The saving is computed on the option's price, which is
 * what the up-front methods pay; the card's installments may add interest.
 * - no split on the card, or installments with no interest: `plain`;
 * - interest on the card: the up-front methods the option really takes, read
 *   like the checkout's method buttons (`allowedPaymentMethods`); card-only,
 *   `card` when 1× on the card has no surcharge;
 * - `null`: no tag (card-only with interest even at 1×, or the card quote is
 *   not in yet, so it is not known whether the card saves as much).
 */
export const savingsVariant = (option: QuotableOption, quote: CardQuote | null | undefined): SavingsVariant | null => {
  if (cardInstallments(option) < 2) return 'plain'
  if (!quote) return null
  if (quote.interestFree) return 'plain'
  const methods = parseAllowedPaymentMethods(option.allowedPaymentMethods)
  if (methods.includes('PIX') && methods.includes('BOLETO')) return 'pixOrBoleto'
  if (methods.includes('PIX')) return 'pix'
  if (methods.includes('BOLETO')) return 'boleto'
  return quote.singleChargeFree ? 'card' : null
}

/* One quote per option, installments and add-ons for the visit: the Store and the offer show several cards. */
const cache = new Map<string, Promise<CardQuote | null>>()

/**
 * The card quote of a yearly option (F-36), from `POST /api/checkout/calculate`
 * like the plan's checkout page: the per-installment amount and the total,
 * interest included when the buyer pays it. Never computed here. `null` while
 * loading, for a monthly option, or when it does not split.
 */
export function useCardQuote(option: QuotableOption | null, addOnProductIds: string[] = []): CardQuote | null {
  const [quote, setQuote] = useState<CardQuote | null>(null)
  const installments = option ? cardInstallments(option) : 1
  const productId = option?.id
  const yearly = isYearly(option)
  const addOnKey = addOnProductIds.join(',')
  useEffect(() => {
    setQuote(null)
    if (!productId || !yearly || installments < 2) return
    const key = `${productId}:${installments}:${addOnKey}`
    let request = cache.get(key)
    if (!request) {
      request = Promise.resolve(
        sdk.checkout.calculatePrice({
          productId,
          paymentMethod: 'CREDIT_CARD',
          installments,
          ...(addOnKey ? { addOnProductIds: addOnKey.split(',') } : {})
        })
      )
        .then((response: CalculatePriceResponse) => {
          const found = response?.installmentOptions?.find((o) => o.installments === installments)
          const single = response?.installmentOptions?.find((o) => o.installments === 1)
          return found
            ? {
                installments,
                each: Number(found.installmentAmount),
                total: Number(found.totalAmount),
                interestFree: response.installmentInterestPayer === 'ORGANIZATION' || Number(found.percent) === 0,
                percent: Number(found.percent),
                singleChargeFree: Boolean(single) && Number(single?.percent) === 0
              }
            : null
        })
        /* Without a quote the card says "em até 12× no cartão" instead of a number. */
        .catch(() => {
          cache.delete(key)
          return null
        })
      cache.set(key, request)
    }
    let alive = true
    void request.then((result) => { if (alive) setQuote(result) })
    return () => {
      alive = false
    }
  }, [productId, yearly, installments, addOnKey])
  return quote
}
